// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { MatTabChangeEvent } from '@angular/material/tabs';
import Element from 'chart.js/dist/core/core.element';
import { Company } from '../../../core/models';
import * as CompanyActions from '../../../core/store/company/company.actions';
import { select, Store } from '@ngrx/store';
import {
  getInfluenceChart,
  getNetworkChart,
  getPerformanceChart,
} from '../../../core/store/company/company.selectors';
import { takeUntil } from 'rxjs/operators';
import { AppState } from '../../../core/store/app.state';
import { Subject } from 'rxjs';
import { ChartPerformance } from '../../../core/models/chart-performance';
import { ChartInfluence } from '../../../core/models/chart-influence';
import { ChartNetwork } from '../../../core/models/chart-network';
import { UserSubscription } from '../../../modules/auth';
import { methodology } from '../../../core/constants/methodology';

@Component({
  selector: 'app-company-map-chart',
  templateUrl: './company-map-chart.component.html',
  styleUrls: ['./company-map-chart.component.scss'],
})
export class CompanyMapChartComponent implements OnInit {
  @Input() company: Company;
  @Input() subscription: UserSubscription | null;
  @ViewChild('tabGroup', { static: false }) tabGroup;
  public yearsChart: number[2000];
  public barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    // We use these empty structures as placeholders for dynamic theming.
    scales: {
      x: { ticks: { color: 'grey' }, grid: { color: 'white' } },
      y: {
        min: 0,
        grace: '10%',
        ticks: { color: 'grey' },
        grid: { color: 'rgba(108,108,108,0.1)' },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
      },
    },
  };

  public pyramidChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    indexAxis: 'y',
    // We use these empty structures as placeholders for dynamic theming.
    scales: {
      x: {
        ticks: { color: 'white' },
        grid: { color: 'white' },
        min: -250,
        max: 250,
        // stacked: true,
        beginAtZero: true,
      },
      y: {
        // beginAtZero: true,
        stacked: true,
        grid: { color: 'white' },
      },
    },
    parsing: {
      xAxisKey: 'normalized_value',
      yAxisKey: 'normalized_value',
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'center',
      },
      tooltip: {
        yAlign: 'bottom',
        callbacks: {
          label: function (context: any) {
            if (context.datasetIndex === 0) {
              const value = Math.abs(context.raw.value);
              return (
                ' ' +
                new Intl.NumberFormat('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }).format(value / 100)
              );
            } else {
              return (
                ' ' +
                new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  notation: 'compact',
                  compactDisplay: 'short',
                  currency: 'USD',
                }).format(context.raw.value)
                // .replace('T', 'K')
              );
            }
          },
          title: function (context) {
            return context[0].dataset.label + ' ' + context[0].label;
          },
        },
      },
    },
  };

  public pieChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 50,
    },
    plugins: {
      legend: {
        display: false,
        position: 'top',
        align: 'end',
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            return (
              ' ' +
              new Intl.NumberFormat('en-US', {
                style: 'percent',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }).format(context.formattedValue / 100)
            );
          },
          title: function (context) {
            return context[0].label;
          },
        },
      },
    },
  };

  pieLabelsLine = {
    id: 'pieLabelsLine',
    afterDraw(chart: Chart) {
      const {
        ctx,
        chartArea: { width, height },
      } = chart;

      if (!chart._metasets[0]) return;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const cx = chart._metasets[0].data[0].x;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const cy = chart._metasets[0].data[0].y;

      chart.data.datasets.forEach((dataset, i) => {
        chart.getDatasetMeta(i).data.forEach((datapoint: Element, index) => {
          const { x: a, y: b } = datapoint.tooltipPosition(true);

          const x = 2 * a - cx;
          const y = 2 * b - cy;

          // draw line
          const halfwidth = width / 2;
          const halfheight = height / 2;
          const xLine = x >= halfwidth ? x + 20 : x - 20;
          const yLine = y >= halfheight ? y + 20 : y - 20;

          const extraLine = x >= halfwidth ? 10 : -10;

          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.arc(x, y, 2, 0, 2 * Math.PI, true);
          ctx.fill();
          ctx.moveTo(x, y);
          ctx.lineTo(xLine, yLine);
          ctx.lineTo(xLine + extraLine, yLine);
          // ctx.strokeStyle = dataset.backgroundColor[index];
          ctx.strokeStyle = 'grey';
          ctx.stroke();

          // text
          ctx.font = '12px Arial';
          // control the position
          const textXPosition = x >= halfwidth ? 'left' : 'right';
          const plusFivePx = x >= halfwidth ? 5 : -5;
          ctx.textAlign = textXPosition;
          ctx.textBaseline = 'middle';
          // ctx.fillStyle = dataset.backgroundColor[index];
          ctx.fillStyle = 'grey';
          const label = `${
            chart.data?.labels?.[index]
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
          }: ${chart.data.datasets[0].data[index].toFixed(0)}%`;
          ctx.fillText(label, xLine + extraLine + plusFivePx, yLine);
        });
      });
    },
  };

  barLabelsLine = {
    id: 'barLabelsLine',
    afterDraw(chart: Chart) {
      const {
        ctx,
        chartArea: { width, height },
      } = chart;

      chart.data.datasets.forEach((dataset, i) => {
        chart.getDatasetMeta(i).data.forEach((datapoint: Element, index) => {
          if (!isNaN(datapoint.height)) {
            const { x: a, y: b } = datapoint.tooltipPosition(true);
            const x = a - 2;
            const y = b - 7;
            ctx.font = '12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = 'grey';
            const label = dataset.data[index].toFixed(3);
            ctx.fillText(label, x, y);
          }
        });
      });
    },
  };
  pyramidLabelsLine = {
    id: 'pyramidLabelsLine',
    afterDraw(chart: Chart) {
      const {
        ctx,
        chartArea: { width, height },
      } = chart;
      const percentageFormatter = new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        notation: 'compact',
        compactDisplay: 'short',
        currency: 'USD',
      });
      chart.data.datasets.forEach((dataset, i) => {
        chart.getDatasetMeta(i).data.forEach((datapoint: Element, index) => {
          const normalized_value = Math.abs(dataset.data[index].value);
          if (normalized_value > 7 && !isNaN(datapoint.width)) {
            const { x: a, y: b } = datapoint.tooltipPosition(true);
            let x = a;
            const y = b;
            ctx.font = 'bold 12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#7a7a7a';
            let label = '';
            if (dataset.label === 'Network Power') {
              label = currencyFormatter.format(dataset.data[index].value);
              // .replace('T', 'K');
              x = a - datapoint.width / 2;
            } else {
              label = percentageFormatter.format(normalized_value / 100);
              x = a + datapoint.width / 2;
            }
            ctx.fillText(label, x, y);
          }
        });
      });
    },
  };
  private readonly destroy$ = new Subject();
  private networkChart: ChartNetwork;
  private influenceChart: ChartInfluence;
  private performanceChart: ChartPerformance;
  private influenceChartYear: number;
  private performanceChartYear: number;

  constructor(private store: Store<AppState>) {}

  public barChartType: ChartType = 'bar';
  public pyramidChartType: ChartType = 'bar';
  public pieChartType: ChartType = 'pie';
  public pyramidChartPlugins = [this.pyramidLabelsLine];
  public barChartPlugins = [this.barLabelsLine];
  public pieChartPlugins = [this.pieLabelsLine];
  public selectedYear = 2000;

  public barChartData: ChartData<'bar'> = {
    labels: ['Overall', 'EBITDA', 'Carbon Intensity', 'TSR', 'Controversies'],
  };

  public pyramidChartData: ChartData<'bar'>;

  public pieChartData: ChartData<'pie'>;
  hideSelectYear = true;

  private generateArrayOfYears(total = 10) {
    const max = new Date().getFullYear();
    const min = max - total;
    const years = [];

    for (let i = max; i >= min; i--) {
      years.push(i);
    }
    return years;
  }

  ngOnInit(): void {
    this.store.dispatch(
      CompanyActions.loadNetworkChart({
        id: this.company.id,
      })
    );
    this.store.dispatch(
      CompanyActions.loadInfluenceChart({
        id: this.company.id,
      })
    );
    this.store.dispatch(
      CompanyActions.loadPerformanceChart({
        id: this.company.id,
      })
    );
    this.store
      .pipe(select(getPerformanceChart))
      .pipe(takeUntil(this.destroy$))
      .subscribe(_performanceChart => {
        if (_performanceChart) {
          this.performanceChart = _performanceChart;
          this.changeBoardPerformanceYear(_performanceChart.chart_years[0]);
        }
      });
    this.store
      .pipe(select(getNetworkChart))
      .pipe(takeUntil(this.destroy$))
      .subscribe(networkChart => {
        if (networkChart)
          this.pyramidChartData = {
            ...this.pyramidChartData,
            labels: networkChart.chart_years,
            datasets: [
              {
                data: networkChart.chart_data.connected_directors,
                label: 'Connected Directors',
                borderRadius: 3,
              },
              {
                data: networkChart.chart_data.network_power,
                label: 'Network Power',
                borderRadius: 3,
              },
            ],
          };
      });
    this.store
      .pipe(select(getInfluenceChart))
      .pipe(takeUntil(this.destroy$))
      .subscribe(_influenceChart => {
        if (_influenceChart) {
          this.influenceChart = _influenceChart;
          this.changeBoardInfluenceYear(_influenceChart.chart_years[0]);
        }
      });
  }

  changeBoardPerformanceYear(year: number) {
    const chart_data = this.performanceChart.chart_data.find(
      item => item.year === year
    );
    const chart_data_other = this.performanceChart.chart_data_other.find(
      item => item.year === year
    );
    this.performanceChartYear = year;
    this.barChartData = {
      ...this.barChartData,
      datasets: [
        {
          data: [
            chart_data.overall,
            chart_data.ebitda,
            chart_data.carbon_intensity,
            chart_data.tsr,
            chart_data.controversies,
          ],
          label: this.company.issuer_name,
          borderRadius: 3,
        },
        {
          data: [
            chart_data_other?.overall,
            chart_data_other?.ebitda,
            chart_data_other?.carbon_intensity,
            chart_data_other?.tsr,
            chart_data_other?.controversies,
          ],
          label: `Other Companies in ${this.company.sector.name}`,
          borderRadius: 3,
        },
      ],
    };
  }

  changeBoardInfluenceYear(year: number) {
    const chart_data = this.influenceChart.chart_data[year]?.slice();

    // Step 1: Sort array in descending order based on influence
    chart_data.sort((a, b) => b.influence - a.influence);

    // Step 2-7: Create a new array with elements sorted in small, large, small, large... order
    const sortedArray = [];
    let left = 0;
    let right = chart_data.length - 1;

    while (left < right) {
      sortedArray.push(chart_data[left]);
      sortedArray.push(chart_data[right]);
      left++;
      right--;
    }

    // If the array length is odd, push the remaining middle element
    if (left === right) {
      sortedArray.push(chart_data[left]);
    }

    this.influenceChartYear = year;
    const labels = sortedArray.map(item =>
      [
        item.director.firstname,
        item.director.middlenames,
        item.director.lastname,
      ].join(' ')
    );

    const data = sortedArray.map(item => item.influence);
    this.pieChartData = {
      labels,
      datasets: [{ data }],
    };
  }

  onTabChanged($event: MatTabChangeEvent) {
    if ($event.index === 1) {
      //board influence
      this.yearsChart = this.influenceChart.chart_years;
      this.selectedYear = this.influenceChartYear;
    } else if ($event.index === 2) {
      //board performance
      this.yearsChart = this.performanceChart.chart_years;
      this.selectedYear = this.performanceChartYear;
    }
    // this.hideSelectYear = $event.index !== 1;
  }

  chartYearSelected($event: Event) {
    if (this.tabGroup.selectedIndex === 1) {
      //board influence
      this.changeBoardInfluenceYear(this.selectedYear);
    } else if (this.tabGroup.selectedIndex === 2) {
      //board performance
      this.changeBoardPerformanceYear(this.selectedYear);
    }
  }

  protected readonly methodology = methodology;
}
